import { ButtonHTMLAttributes, ChangeEvent, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { useFormikContext } from 'formik';
import IconButton from '../basics/IconButton';
import XIcon from '../icons/XIcon';
import InfoIcon from '../icons/InfoIcon';
import { UncontrolledWhiteTooltip } from '../basics/WhiteTooltip';

const IMAGE_INFO =
  'This image will be displayed in project previews and PDF exports. For best results, use an image with roughly square dimensions.';

interface UploadTeamImageProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  imageUrl: Nullable<string>;
}

// Subset of form values relevant for this component
interface TeamImageValues {
  deleteImage: Nullable<boolean>;
  file: Nullable<File>;
  hasImage: boolean;
  imageExtension: Nullable<string>;
}

const UploadTeamImageButton = ({
  imageUrl,
  ...props
}: UploadTeamImageProps) => {
  const {
    isSubmitting,
    values,
    setValues,
  } = useFormikContext<TeamImageValues>();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<Nullable<string>>(null);
  const [fileName, setFileName] = useState<Nullable<string>>(null);
  const [preview, setPreview] = useState<Nullable<string>>(imageUrl);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Clear values from previous image selection, if present
    setError(null);
    setFileName(null);
    setPreview(null);

    const reader = new FileReader();
    const file = e.target.files?.[0];
    const imageExtension = file?.name?.match(/\.[^/.]+$/)?.[0] || '';
    const fileSize = file?.size || undefined;
    if (fileSize && fileSize > 2000000) {
      setError('Image cannot be larger than 2MB');
      return;
    }

    reader.addEventListener('load', (event) => {
      if (typeof event.target?.result === 'string') {
        setFileName(file?.name);
        setPreview(event.target.result);
      }
    });

    if (file) {
      reader.readAsDataURL(file);
      setValues({
        ...values,
        deleteImage: undefined,
        file,
        hasImage: true,
        imageExtension,
      });
    }
  };

  const onRemoveImage = () => {
    // Clear fileRef value to handle user uploading same image twice
    if (fileRef.current?.value) fileRef.current.value = '';
    setFileName(null);
    setPreview(null);
    setValues({
      ...values,
      deleteImage: imageUrl ? true : undefined,
      file: undefined,
      hasImage: false,
      imageExtension: null,
    });
  };

  return (
    <>
      <div className="d-flex align-items-center">
        <IconButton
          disabled={isSubmitting}
          onClick={() => fileRef.current?.click()}
          {...props}
        >
          Upload Team Logo{' '}
          <InfoIcon id="image-info" className="ml-2 text-muted" />
          <UncontrolledWhiteTooltip
            innerClassName="font-small"
            modifiers={{ preventOverflow: { boundariesElement: 'viewport' } }}
            placement="right"
            target="image-info"
          >
            {IMAGE_INFO}
          </UncontrolledWhiteTooltip>
        </IconButton>
        {error && <span className="text-danger mx-2">{error}</span>}
        {preview && (
          <>
            <span className="text-muted mx-2">
              {fileName || 'Remove Image'}
            </span>
            <Button className="p-0" color="outline" onClick={onRemoveImage}>
              <XIcon fill="rgba(33, 33, 33, 0.5)" width={10} height={10} />
            </Button>
          </>
        )}
      </div>
      <input
        type="file"
        ref={fileRef}
        className="d-none"
        accept="image/bmp,image/jpeg,image/png"
        onChange={onChange}
      />
      {preview && (
        <div className="mt-3">
          <img src={preview} height="150" alt="Team logo preview" />
        </div>
      )}
    </>
  );
};

export default UploadTeamImageButton;
